import { useEffect } from 'react';

import { INVOICE_NOT_EMAILABLE_MESSAGE } from '@/_pages/invoices/shared/components/invoice-email-actions-cell';
import { hasParentBillingContact } from '@/_pages/invoices/shared/has-parent-billing-contact';
import type { InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas';
import { snackbar } from '@/_shared';

type UseInvoiceEmailShortcutOptions = {
  sendingInvoiceId: number | null;
  onOpenEmailModal: (invoice: InvoiceQuerySchema) => void;
} & (
  | {
      activeInvoice: InvoiceQuerySchema | null;
      activeInvoiceId?: never;
      invoices?: never;
    }
  | {
      activeInvoice?: undefined;
      activeInvoiceId: string | null;
      invoices: InvoiceQuerySchema[] | undefined;
    }
);

/**
 * ⌘E / Ctrl+E opens the send-invoice-email modal for the highlighted list row.
 * Uses capture phase + stopImmediatePropagation so the global sidebar ⌘E shortcut does not run.
 * Ignored while focus is in an input, textarea, select, or contenteditable.
 */
export function useInvoiceEmailShortcut({
  activeInvoice,
  activeInvoiceId,
  invoices,
  sendingInvoiceId,
  onOpenEmailModal,
}: UseInvoiceEmailShortcutOptions) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.code !== 'KeyE' || e.altKey) {
        return;
      }

      if (e.repeat) {
        return;
      }

      const el = e.target as HTMLElement | null;
      if (el?.closest?.('input, textarea, select, [contenteditable="true"]')) {
        return;
      }

      if (sendingInvoiceId != null) {
        return;
      }

      const inv =
        activeInvoice ??
        (() => {
          const id = activeInvoiceId;
          if (!id || !invoices?.length) {
            return null;
          }
          return invoices.find((invoice) => String(invoice.id) === id) ?? null;
        })();

      if (!inv) {
        return;
      }

      if (!hasParentBillingContact(inv)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        snackbar.error(INVOICE_NOT_EMAILABLE_MESSAGE);
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      onOpenEmailModal(inv);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeInvoice, activeInvoiceId, invoices, onOpenEmailModal, sendingInvoiceId]);
}
